import React, {useContext, useState, useEffect, useRef} from 'react';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import {StyleSheet, ScrollView, View, Platform, Linking} from 'react-native';
import MainConsumerContext from '../../../MainConsumerContext';
import AppRoutes from 'src/AppRoutes';
import Events from 'src/logging/Events';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import PassKit from 'src/nativeModules/PassKit';
import SettingsMenuOption from '../../../elements/newui/SettingsMenuOption';
import SettingsMenuSection from '../../../elements/newui/SettingsMenuSection';
import Styles from '../../../Styles';
import ActionsFactory from 'src/actions/ActionsFactory';
import AVText from '../../../elements/AVText';
import AccountStore from 'src/stores/AccountStore';
import Localized from 'src/constants/AppStrings';
import {alertError} from '../../../helpers/AlertHelper';
import {connect} from 'react-redux';
import {RootState} from 'src/redux/store';
import {PaymentCredentials} from 'src/models/PaymentCredentials';
import uuid from '../../../../nativeModules/UUID';
import UserProfileInitials from 'src/components/elements/UserProfileInitials';
import CommonUtils from './commonutils';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import Settings from 'src/Settings';
import {authStore} from 'src/init';
import {getDescriber as getSnackDescriber} from 'src/components/screens/sendSnack/descriptor/sendasnack/DescriptorType';

import {getDescriber} from '../descriptor/DescriptorType';

import BaseScreen from 'src/components/screens/BaseScreen';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import UIManager from 'src/components/elements/ui/UIManager';
import Logger from 'src/logging/Logger';
import {useGlobalize} from '../../../../i18n/hook/useGlobalize';

const AccountScreenNewUI: React.FC<AccountScreenNewUIProps> = (props) => {
  const context = useContext(MainConsumerContext);
  const [email, setEmail] = useState(AccountStore.getEmail());

  const {formatCurrency} = useGlobalize();

  const [name, setName] = useState(AccountStore.getFullName());
  const [fullVersion, setFullVersion] = useState('');

  const [locationName, setLocationName] = useState(
    AccountStore.getLocationName(),
  );
  const [isSSO, setIsSSO] = useState(false);
  const currentPolicy = useRef('');
  const [optionItems, setOptionItems] = useState<
    Record<
      string,
      Array<{
        accessibilityLabel: string;
        accessibilityHint: string;
        onPressed: () => void;
        label: string;
      }>
    >
  >({});
  const currentPolicyType = useRef('');

  useEffect(() => {
    initComponentDidMount();
    initOptionItems();
  }, []);

  useEffect(() => {
    if (props.emailChanged) {
      ActionsFactory.getAccountActions().setIntentionalLogout(true);
      ActionsFactory.getAccountActions()
        .logout()
        .finally(() => {
          context.actions.showModal(
            Localized.Success.your_new_email_has_been_updated_in_the_app,
            Localized.Success.success,
            Localized.Buttons.okay,
            Styles.Colors.PayNew.primary01,
            2,
            false,
            false,
            undefined,
            () => {
              context?.actions.changeRootRoute(AppRoutes.Login);
            },
          );
        });
    }
  }, [context.actions, props.emailChanged]);

  const onRefresh = async () => {
    FirebaseAnalytic.trackEvent('onRefresh', 'AccountScreenNewUI', {
      ...props,
    });
    try {
      await ActionsFactory.getAccountActions().reloadConsumerData({
        accountId: AccountStore.getAccountId(),
        accountBalanceId: AccountStore.getAccountBalanceId(),
        email: AccountStore.getEmail(),
        userInitiated: true,
      });
    } catch (error) {
      const guid = await uuid.getRandomUUID();
      CrashlyticsEvents.log(
        'Exception',
        'AccountScreenNewUI:onRefresh',
        generateErrorMessage(error),
        guid,
        {
          ...props,
        },
      );
      Events.Error.trackEvent(
        'Exception',
        'AccountScreenNewUI:onRefresh',
        generateErrorMessage(error),
        guid,
        {
          ...props,
        },
      );
    }
  };

  const addPassClicked = async () => {
    context?.actions.showSpinner();
    try {
      const success = await PassKit.addPass(context?.state.passUrl ?? '');
      if (success) {
        const passExists = await PassKit.doesPassExist(
          context?.state.passUrl ?? '',
        );

        if (passExists) {
          if (context?.actions.passAdded) {
            context?.actions.passAdded();
          }

          Events.Pass.trackEvent();
        }
      } else {
        alertError(Localized.Errors.failed_to_add_pass);
      }
    } finally {
      context?.actions.hideSpinner();
    }
  };

  const onAccountChange = () => {
    let response;
    try {
      response = AccountStore.getPrivacyVersionResponse();

      if (response && response.status === 'ok') {
        currentPolicyType.current = response.type;
        currentPolicy.current = response.version;
      }
      setName(AccountStore.getFullName());
      setEmail(AccountStore.getEmail());
      setLocationName(AccountStore.getLocationName());
    } catch (error) {
      CrashlyticsEvents.log(
        'Exception',
        'AccountScreen:onAccountChange',
        generateErrorMessage(error),
        response,
      );
      Events.Error.trackEvent(
        'Exception',
        'AccountScreen:onAccountChange',
        generateErrorMessage(error),
      );
    }
  };

  useEffect(() => {
    const fetchIsSSO = async () => {
      try {
        const isSSOValue = await authStore.getIsSSO();
        setIsSSO(isSSOValue);
      } catch (error) {
        CrashlyticsEvents.log(
          'Exception',
          'AccountScreen:fetchIsSSO',
          generateErrorMessage(error),
        );
        Events.Error.trackEvent(
          'Exception',
          'AccountScreen:fetchIsSSO',
          generateErrorMessage(error),
        );
      }
    };
    fetchIsSSO();
  });

  const handlePrivacyPolicyUrl = () => {
    CommonUtils.handlePrivacyPolicy(
      currentPolicy.current,
      currentPolicyType.current,
    );
  };

  const getPaymentOptions = () => {
    const payment = [];
    payment.push({
      accessibilityLabel: Localized.Labels.wallet,
      accessibilityHint: 'Double tap to view your wallet',
      onPressed: CommonUtils.toWalletScreen,
      label: Localized.Labels.wallet,
      testID: 'qa_btn_Wallet',
    });
    return payment;
  };

  const getLinkAccountsOptions = () => {
    const linkAccounts: Array<object> = [];
    const label = AccountStore.getMarketCard()
      ? Localized.Labels.edit_market_card
      : Localized.Labels.scan_market_card;
    linkAccounts.push({
      accessibilityLabel: label,
      accessibilityHint: 'Double tap to scan a market card',
      onPressed: CommonUtils.handleMarketCard,
      label: label,
      testID: 'qa_btn_ScanMarketCard',
    });
    linkAccounts.push({
      accessibilityLabel: Localized.Labels.scan_kiosk_qr_code,
      accessibilityHint: 'Double tap to scan kiosk qr code',
      onPressed: CommonUtils.scanKioskQrCode, //To be done as part of SOS-58257
      label: Localized.Labels.scan_kiosk_qr_code,
      testID: 'qa_btn_ScanKioskQRCode',
    });
    return linkAccounts;
  };

  const getHelpAndOtherOptions = () => {
    let helpAndOther = [];
    helpAndOther = [
      {
        accessibilityLabel: UIManager.getHelpLabel(),
        accessibilityHint: 'Double tap to navigate to the help screen',
        onPressed: CommonUtils.contactClicked,
        label: UIManager.getHelpLabel(),
        testID: 'qa_btn_Help',
      },
      {
        accessibilityLabel: Localized.Labels.change_email,
        accessibilityHint: 'Double tap to change your email',
        onPressed: CommonUtils.handleChangeEmail,
        label: Localized.Labels.change_email,
        testID: 'qa_btn_ChangeEmail',
      },
      {
        accessibilityLabel: Localized.Labels.change_pin,
        accessibilityHint: 'Double tap to change your pin',
        onPressed: CommonUtils.handleChangePin,
        label: Localized.Labels.change_pin,
        testID: 'qa_btn_ChangePin',
      },
    ];
    return helpAndOther;
  };

  const getSettingsOptions = () => {
    const settings = [];
    settings.push({
      accessibilityLabel: Localized.Labels.personal_info,
      accessibilityHint:
        'Double tap to view or update your personal information',
      onPressed: CommonUtils.toManageAccount,
      label: Localized.Labels.personal_info,
      testID: 'qa_btn_PersonalInfo',
    });
    settings.push({
      accessibilityLabel: Localized.Labels.link_location,
      accessibilityHint: 'Double tap to go to link location',
      onPressed: CommonUtils.handleLinkLocation,
      label: Localized.Labels.link_location,
      testID: 'qa_btn_LinkLocation',
    });
    if (
      AccountStore.isConsumerSendSnackEnabled() &&
      AccountStore.isSendSnackEnabledForLocation()
    ) {
      settings.push({
        accessibilityLabel: Localized.Labels.blocked_users,
        accessibilityHint: 'Double tap to navigate to the blocked users screen',
        onPressed: CommonUtils.blockedUsersClicked,
        label: Localized.Labels.blocked_users,
        testID: 'qa_btn_BlockedUsers',
      });
    }
    settings.push({
      accessibilityLabel: Localized.Labels.view_privacy_policy,
      accessibilityHint: 'Double tap to view the privacy policy',
      onPressed: handlePrivacyPolicyUrl,
      label: Localized.Labels.view_privacy_policy,
      testID: 'qa_btn_ViewPrivacyPolicy',
    });
    settings.push({
      accessibilityLabel: Localized.Labels.view_terms_and_conditions,
      accessibilityHint: 'Double tap to view terms and conditions',
      onPressed: CommonUtils.handleTerms,
      label: Localized.Labels.view_terms_and_conditions,
      testID: 'qa_btn_ViewTermsAndConditions',
    });
    settings.push({
      accessibilityLabel: Localized.Labels.delete_account,
      accessibilityHint: 'Double tap to navigate to the delete account screen',
      onPressed: CommonUtils.handleDeleteAccount,
      label: Localized.Labels.delete_account,
      testID: 'qa_btn_DeleteAccount',
    });
    if (Logger.Log.isLogEnabled()) {
      settings.push({
        accessibilityLabel: Localized.Labels.send_logs_to_app_developers,
        accessibilityHint: 'Double tap to send logs to app developers',
        onPress: () => Logger.Log.shareLogFile(),
        label: Localized.Labels.send_logs_to_app_developers,
        isShareFile: true,
        testID: 'qa_btn_SendLogs',
      });
    }
    return settings;
  };

  const getHistoryOptions = () => {
    const settings = [];
    settings.push({
      accessibilityLabel: Localized.Labels.receipts,
      accessibilityHint: 'Double tap to view receipts',
      onPressed: CommonUtils.toPurchaseHistoryScreen,
      label: Localized.Labels.receipts,
      testID: 'qa_btn_Receipts',
    });
    return settings;
  };

  const getSignOutButton = (onLogoutSelect: () => void): JSX.Element => {
    return (
      <React.Fragment>
        <RoundedAppButton
          titleText={Localized.Buttons.signout}
          onPress={onLogoutSelect}
          buttonViewStyle={styles.btnViewStyle}
          buttonContainerStyle={{marginTop: Styles.Spacing.m2}}
          buttonType={RoundedAppButtonType.Outline}
          accessibilityLabelValue={Localized.Buttons.signout}
          accessibilityHintValue="Double tap to log out"
          aria-label={Localized.Buttons.signout}
          textStyle={styles.btnText}
          testID="qa_btn_SignOut"
        />
      </React.Fragment>
    );
  };

  const {
    getParentContainerStyle,
    showIcon,
    getUserProfileTextStyleDescriptors,
    getGiftingOptions,
    getBrandTitle,
    getBrandWebPage,
  } = getDescriber();

  const brandWebPage = getBrandWebPage();
  const brandTitle = getBrandTitle();

  const initComponentDidMount = async () => {
    FirebaseAnalytic.trackEvent('componentDidMount', 'AccountScreen', {
      ...props,
    });

    const version = await Settings.getFullDisplayVersion(Platform.OS);
    setFullVersion(version);
    try {
      const response = AccountStore.getPrivacyVersionResponse();

      if (
        (response && response.status === 'ok') ||
        (response &&
          Object.keys(response).length > 0 &&
          response.hasOwnProperty('version'))
      ) {
        currentPolicy.current = response.version;
        currentPolicyType.current = response.type;
      }

      AccountStore.addChangeListener(onAccountChange);
    } catch (error) {
      const guid = await uuid.getRandomUUID();
      CrashlyticsEvents.log(
        'Exception',
        'AccountScreenNewUI:componentDidMount',
        generateErrorMessage(error),
        guid,
      );
      Events.Error.trackEvent(
        'Exception',
        'AccountScreenNewUI:componentDidMount',
        generateErrorMessage(error),
        guid,
      );
    }
    setIsSSO(false);
  };

  const openWebPage = () => {
    Linking.openURL(brandWebPage);
  };

  const initOptionItems = () => {
    const referralAmount = formatCurrency(
      AccountStore.getReferralCampaign()?.amount ?? 0,
      AccountStore.getCurrency(),
    );
    const giftingOptions = getGiftingOptions(referralAmount) as {
      accessibilityLabel: string;
      accessibilityHint: string;
      onPressed: () => void;
      label: string;
    }[];

    const settings = getSettingsOptions();
    const historyOptions = getHistoryOptions() || [];
    const paymentOptions = getPaymentOptions() || [];
    const helpAndOther = getHelpAndOtherOptions();
    const linkAccountsOptions = getLinkAccountsOptions() || [];
    const allOptions = {
      giftingOptions,
      historyOptions,
      paymentOptions,
      settings,
      helpAndOther,
      linkAccountsOptions,
    };
    setOptionItems(allOptions);
  };

  const onLogoutSelect = async () => {
    context?.actions.showSpinner();
    Events.Logout.trackEvent();
    await ActionsFactory.getAccountActions().logout();
    ActionsFactory.getAccountActions().setIntentionalLogout(true);
    context?.actions.hideSpinner();
    context?.actions.changeRootRoute(AppRoutes.Login);
  };

  return (
    <BaseScreen
      title={Localized.Labels.account}
      accessibilityLabel={Localized.Labels.account}
      hideBack={true}
    >
      <ScrollView style={getParentContainerStyle()}>
        <View style={styles.container}>
          <UserProfileInitials
            accessibilityLabel={`Name Initials: ${name}, text`}
            accessibilityRole="text"
            aria-label={`Initials Name: ${name}, text`}
            text={name}
          />
          <View style={styles.content}>
            <AVText
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
              accessible={true}
              accessibilityLabel={`Full Name: ${name}, text`}
              accessibilityRole="text"
              ellipsizeMode="tail"
              numberOfLines={1}
              aria-label={`Full Name: ${name}, text`}
              style={[
                styles.nameText,
                getUserProfileTextStyleDescriptors().name,
                getSnackDescriber().snackDetailsBtnFontSTyle(),
              ]}
              testID="qa_UserName_txt"
              nativeID="qa_UserName_txt"
            >
              {name}
            </AVText>
            <AVText
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
              accessible={true}
              accessibilityLabel={`Email: ${email}`}
              accessibilityRole="text"
              numberOfLines={1}
              aria-label={`Email: ${email}, text`}
              style={[
                styles.emailText,
                getUserProfileTextStyleDescriptors().emailLocationText,
                getSnackDescriber().snackDetailsBtnFontSTyle(),
              ]}
              testID="qa_UserEmail_txt"
              nativeID="qa_UserEmail_txt"
            >
              {email}
            </AVText>
            <AVText
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
              accessible={true}
              accessibilityLabel={`Market Location: ${locationName}`}
              accessibilityRole="text"
              numberOfLines={1}
              aria-label={`Market Location: ${locationName}, text`}
              style={[
                styles.locationText,
                getUserProfileTextStyleDescriptors().emailLocationText,
                getUserProfileTextStyleDescriptors().locationView,
                getSnackDescriber().snackDetailsBtnFontSTyle(),
              ]}
              testID="qa_UserHomeLocation_txt"
              nativeID="qa_UserHomeLocation_txt"
            >
              {locationName}
            </AVText>
          </View>
        </View>

        {optionItems.giftingOptions &&
          optionItems.giftingOptions.length > 0 && (
            <SettingsMenuSection title={Localized.Labels.gifting_title}>
              <SettingsMenuOption listItems={optionItems.giftingOptions} />
            </SettingsMenuSection>
          )}

        {!AccountStore.isDemo() &&
          optionItems.paymentOptions &&
          optionItems.paymentOptions.length > 0 && (
            <>
              <SettingsMenuSection
                title={Localized.Labels.payment_account_section}
              >
                <SettingsMenuOption listItems={optionItems.paymentOptions} />
              </SettingsMenuSection>
              {!context?.state.passExists && context?.state.canAddPass && (
                <RoundedAppButton
                  buttonViewStyle={styles.addPassBtnView}
                  buttonContainerStyle={styles.addPassContainer}
                  textStyle={[
                    styles.roundButtonText,
                    getUserProfileTextStyleDescriptors().versionFontstyle,
                    getSnackDescriber().snackDetailsBtnFontSTyle(),
                  ]}
                  titleText={Localized.Buttons.add_to_phone_wallet}
                  accessibilityLabelValue={
                    Localized.Buttons.add_to_phone_wallet
                  }
                  buttonType={RoundedAppButtonType.Outline}
                  onPress={() => addPassClicked()}
                />
              )}
            </>
          )}

        {optionItems.historyOptions &&
          optionItems.historyOptions.length > 0 && (
            <SettingsMenuSection title={Localized.Labels.transaction_history}>
              <SettingsMenuOption listItems={optionItems.historyOptions} />
            </SettingsMenuSection>
          )}

        {optionItems.linkAccountsOptions &&
          optionItems.linkAccountsOptions.length > 0 && (
            <SettingsMenuSection title={Localized.Labels.link_accounts}>
              <SettingsMenuOption listItems={optionItems.linkAccountsOptions} />
            </SettingsMenuSection>
          )}

        {optionItems.helpAndOther && optionItems.helpAndOther.length > 0 && (
          <SettingsMenuSection title={Localized.Labels.support}>
            <SettingsMenuOption
              listItems={
                isSSO
                  ? optionItems.helpAndOther.filter(
                      (item) => item.label !== Localized.Labels.change_email,
                    )
                  : optionItems.helpAndOther
              }
            />
          </SettingsMenuSection>
        )}

        {optionItems.settings && optionItems.settings.length > 0 && (
          <SettingsMenuSection title={Localized.Labels.settings}>
            <SettingsMenuOption listItems={optionItems.settings} />
          </SettingsMenuSection>
        )}

        <View style={styles.signoutBtn}>
          {getSignOutButton(onLogoutSelect)}
          {showIcon(openWebPage, brandTitle, brandWebPage)}
          <AVText
            style={[
              styles.appVersionText,
              getSnackDescriber().snackDetailsBtnFontSTyle(),
              getUserProfileTextStyleDescriptors().versionFontstyle,
            ]}
            accessible={true}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
            accessibilityLabel={`Application version: ${fullVersion}`}
            accessibilityRole="text"
            aria-label={`Application version: ${fullVersion}, text`}
          >
            {fullVersion}
          </AVText>
        </View>
      </ScrollView>
    </BaseScreen>
  );
};

// Set default props if necessary
AccountScreenNewUI.defaultProps = {
  passExists: false,
  canAddPass: false,
  rewards: 0,
  passUrl: '',
};

type AccountScreenNewUIProps = Record<string, unknown> & {
  paymentCredentials?: PaymentCredentials;
  emailChanged?: boolean;
};

const styles = StyleSheet.create({
  notificationLogo: {
    right: Styles.Spacing.m2,
    width: Styles.Heights.touchTargetHeight1,
    height: Styles.Heights.touchTargetHeight1,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: Styles.Heights.h1 - 5,
  },
  container: {
    flexDirection: 'row',
    marginHorizontal: Styles.Spacing.m3,
    marginTop: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  content: {
    paddingLeft: 22,
    flex: 1,
  },
  signoutBtn: {
    paddingHorizontal: Styles.Spacing.m3,
    marginTop: Styles.Spacing.m3,
    marginBottom: Styles.Spacing.m3,
  },
  emailText: {
    color: Styles.darkColor,
    fontSize: Styles.Fonts.f8,
    paddingTop: 6,
    fontWeight: '400',
  },
  locationText: {
    color: Styles.darkColor,
    fontSize: Styles.Fonts.f8,
    fontWeight: '400',
    paddingTop: 6,
  },
  nameText: {
    color: Styles.darkColor,
    fontSize: Styles.Fonts.f7,
    fontWeight: '600',
    marginTop: Styles.Spacing.m1,
  },
  appVersionText: {
    color: Styles.darkColor,
    fontSize: Styles.Fonts.f1,
    marginBottom: Styles.Spacing.m2,
    marginTop: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  revolveMarketSingleLineLogo: {
    marginTop: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  fiveStarLink: {
    marginTop: Styles.Spacing.m1,
    fontSize: Styles.Fonts.f7,
    textDecorationLine: 'underline',
    color: '#2C6CD3',
    fontWeight: '600',
  },
  roundButtonText: {
    fontWeight: '700',
    fontSize: Styles.Fonts.f1,
  },
  btnViewStyle: {
    height: Styles.Heights.touchTargetHeight1 - 6,
    borderWidth: 2,
    backgroundColor: Styles.white,
  },
  btnText: {
    fontFamily: Styles.FontFamily.aeonikRegular,
    fontWeight: '700',
    fontSize: Styles.Fonts.f1,
  },
  addPassBtnView: {
    borderWidth: 2,
    backgroundColor: Styles.white,
  },
  addPassContainer: {
    marginLeft: Styles.Spacing.m3,
    marginTop: Styles.Spacing.m3 + Styles.Spacing.m1,
  },
});
const ConnectedAccount = connect((state: RootState) => ({
  paymentCredentials: state.account.paymentCredentials,
}))(AccountScreenNewUI);
export default withForwardedNavigationParams<AccountScreenNewUIProps>()(
  ConnectedAccount,
);
